import React, { useState } from 'react';
import { FaPhoneAlt, FaSearch, FaUserMd, FaStethoscope, FaCalendarAlt, FaClipboardList, FaAmbulance, FaShieldAlt } from 'react-icons/fa';
import { BsTelephoneForward, BsWhatsapp } from 'react-icons/bs';

const EnhancedContactSteps = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const cards = [
    {
      step: "01",
      icon: <FaPhoneAlt />,
      title: "संपर्क करें",
      subTitle: "Contact Us",
      description: "Appointment बुक करने के लिए कॉल करें",
      details: "80022 80222 | 80024 80222",
      features: ["Instant Response", "24/7 Available", "WhatsApp Support"],
      buttonText: "Call Now",
      color: "sky",
      delay: 100
    },
    {
      step: "02",
      icon: <FaSearch />,
      title: "जाँच करें",
      subTitle: "Diagnose",
      description: "समस्या की जड़ तक पहुँचे",
      details: "Advanced Tests & Diagnosis",
      features: ["Accurate Diagnosis", "Same Day Reports", "Modern Equipment"],
      buttonText: "View Tests",
      color: "red",
      delay: 200
    },
    {
      step: "03",
      icon: <FaUserMd />,
      title: "परामर्श लें",
      subTitle: "Consult",
      description: "विशेषज्ञ से परामर्श",
      details: "Kidney, Prostate & Urine Problems",
      features: ["Senior Urologist", "Personalized Care", "Follow-up"],
      buttonText: "Book Consultation",
      color: "sky",
      delay: 300
    },
    {
      step: "04",
      icon: <FaStethoscope />,
      title: "इलाज करें",
      subTitle: "Treatment",
      description: "किडनी, प्रोस्टेट समस्याओं का इलाज",
      details: "Trusted Urological Care",
      features: ["Minimal Invasive", "High Success Rate", "Post-op Care"],
      buttonText: "View Treatments",
      color: "red",
      delay: 400
    }
  ];

  const quickLinks = [
    { icon: <BsTelephoneForward />, label: "Direct Call", number: "80022 80222", color: "sky" },
    { icon: <BsWhatsapp />, label: "WhatsApp", number: "Message Now", color: "green" },
    { icon: <FaCalendarAlt />, label: "Online Booking", number: "Instant Slot", color: "red" },
    { icon: <FaAmbulance />, label: "Emergency", number: "24/7 Available", color: "orange" }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-white to-blue-50/20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <div className="inline-flex items-center mb-4 px-5 py-2 bg-gradient-to-r from-sky-50 to-red-50 rounded-full border border-sky-200 shadow-sm">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
              <div className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-pulse delay-75"></div>
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse delay-150"></div>
              <div className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-pulse delay-300"></div>
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
            आसान <span className="bg-gradient-to-r from-sky-600 to-red-500 bg-clip-text text-transparent">कदम</span> में पूरा इलाज
          </h2>
          <p className="text-gray-600 text-sm">
            Complete urological care process in four simple steps
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((card, index) => (
            <div 
              key={index}
              className="relative group"
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className={`relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 transform ${
                hoveredCard === index ? '-translate-y-2 scale-105' : ''
              } border border-gray-100 overflow-hidden h-full`}>
                <div className={`absolute top-3 left-3 w-10 h-10 rounded-full ${
                  card.color === 'sky' 
                    ? 'bg-gradient-to-br from-sky-500 to-blue-600' 
                    : 'bg-gradient-to-br from-red-500 to-pink-600'
                } flex items-center justify-center z-10 shadow-lg`}>
                  <span className="text-white text-sm font-bold">{card.step}</span>
                </div>
                <div className={`h-1.5 w-full ${
                  card.color === 'sky' 
                    ? 'bg-gradient-to-r from-sky-400 to-blue-500' 
                    : 'bg-gradient-to-r from-red-400 to-pink-500'
                }`}></div>
                <div className="p-4">
                  <div className={`w-16 h-16 rounded-xl ${
                    card.color === 'sky' 
                      ? 'bg-gradient-to-br from-sky-100 to-blue-100' 
                      : 'bg-gradient-to-br from-red-100 to-pink-100'
                  } flex items-center justify-center mb-4 mx-auto transform transition-all duration-300 ${
                    hoveredCard === index ? 'rotate-6 scale-110' : ''
                  }`}>
                    <div className={`${card.color === 'sky' ? 'text-sky-600' : 'text-red-600'} text-2xl`}>
                      {card.icon}
                    </div>
                  </div>
                  <h3 className="text-center text-lg font-bold text-gray-800 mb-1">
                    {card.title}
                  </h3>
                  <p className={`text-center text-xs font-medium mb-2 ${
                    card.color === 'sky' ? 'text-sky-500' : 'text-red-500'
                  }`}>
                    {card.subTitle}
                  </p>
                  <p className="text-center text-xs text-gray-600 mb-3 leading-relaxed">
                    {card.description}
                  </p>
                  <div className={`p-2 rounded-lg mb-3 ${
                    card.color === 'sky' ? 'bg-sky-50' : 'bg-red-50'
                  }`}>
                    <p className={`text-center font-bold ${
                      card.color === 'sky' ? 'text-sky-600' : 'text-red-600'
                    } text-sm`}>
                      {card.details}
                    </p>
                  </div>
                  <div className="mb-4 space-y-1.5">
                    {card.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-xs">
                        <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                          card.color === 'sky' ? 'bg-sky-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <button className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all duration-300 ${
                    card.color === 'sky' 
                      ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg' 
                      : 'bg-gradient-to-r from-red-500 to-pink-600 text-white hover:shadow-lg'
                  } transform hover:scale-105`}>
                    {card.buttonText}
                  </button>
                </div>
                <div className={`absolute inset-0 rounded-xl ${
                  card.color === 'sky' 
                    ? 'bg-gradient-to-br from-sky-100/10 to-blue-100/5' 
                    : 'bg-gradient-to-br from-red-100/10 to-pink-100/5'
                } opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10`}></div>
              </div>
            </div>
          ))}
        </div>
        <div className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickLinks.map((link, index) => (
              <a
                key={index}
                href={link.label === "Direct Call" ? "tel:+918002280222" : "#"}
                target={link.label === "WhatsApp" ? "_blank" : "_self"}
                rel={link.label === "WhatsApp" ? "noopener noreferrer" : ""}
                className={`group p-3 rounded-xl border transition-all duration-300 ${
                  link.color === 'sky' 
                    ? 'border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50 hover:border-sky-400' 
                    : link.color === 'green'
                    ? 'border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 hover:border-green-400'
                    : link.color === 'red'
                    ? 'border-red-200 bg-gradient-to-br from-red-50 to-pink-50 hover:border-red-400'
                    : 'border-orange-200 bg-gradient-to-br from-orange-50 to-yellow-50 hover:border-orange-400'
                }`}
              >
                <div className="flex items-center justify-center mb-2">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 ${
                    link.color === 'sky' 
                      ? 'bg-gradient-to-r from-sky-500 to-blue-600' 
                      : link.color === 'green'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                      : link.color === 'red'
                      ? 'bg-gradient-to-r from-red-500 to-pink-600'
                      : 'bg-gradient-to-r from-orange-500 to-yellow-600'
                  }`}>
                    <div className="text-white text-lg">
                      {link.icon}
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-gray-800 text-sm mb-0.5">
                    {link.label}
                  </div>
                  <div className={`text-xs font-semibold ${
                    link.color === 'sky' 
                      ? 'text-sky-600' 
                      : link.color === 'green'
                      ? 'text-green-600'
                      : link.color === 'red'
                      ? 'text-red-600'
                      : 'text-orange-600'
                  }`}>
                    {link.number}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute left-4 top-1/3 hidden lg:block">
        <div className="relative animate-float-slow">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-sky-400/20 to-blue-500/20 flex items-center justify-center">
            <FaPhoneAlt className="text-sky-400/40" />
          </div>
        </div>
      </div>

      <div className="absolute right-4 bottom-1/3 hidden lg:block">
        <div className="relative animate-float-slow" style={{animationDelay: '1.5s'}}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-400/20 to-pink-500/20 flex items-center justify-center">
            <FaUserMd className="text-red-400/40" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnhancedContactSteps;